import { getApiBaseUrl } from '../redux/WithcookieSlice';

// 메모리 캐시 추가
const apiCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5분

// 보유한 스킨 조회
export const fetchOwnedSkins = async (type = 'LAYOUT', category = null) => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    let url = `${apiBaseUrl}/api/skins/owned?type=${type}`;
    
    if (category) {
      url += `&category=${category}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`API 요청 실패: ${response.status}`);
    }
    
    const data = await response.json();
    return data.skins || [];
  } catch (error) {
    console.error('보유한 스킨 조회 오류:', error);
    throw error;
  }
};

// 스킨 검색 (구매 가능한 모든 스킨)
export const searchSkins = async (type = 'LAYOUT', query = '', category = null) => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    let url = `${apiBaseUrl}/api/skins/search?type=${type}`;
    
    if (query) {
      url += `&q=${encodeURIComponent(query)}`;
    }
    
    if (category) {
      url += `&category=${category}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`API 요청 실패: ${response.status}`);
    }
    
    const data = await response.json();
    const skins = data.skins || [];
    

    const validatedSkins = skins.map(skin => {
      const validation = {
        id: !!skin.id,
        name: !!skin.name,
        hasPermission: skin.hasPermission !== undefined,
        latestVersion: !!skin.latestVersion,
        jsFileUrl: !!(skin.latestVersion && skin.latestVersion.jsFileUrl),
        cssFileUrl: !!(skin.latestVersion && skin.latestVersion.cssFileUrl)
      };
      
      if (!validation.jsFileUrl && skin.hasPermission) {
        console.warn(`⚠️ 권한이 있는 스킨 ${skin.name}(ID: ${skin.id})에 JS 파일 URL이 없습니다`, {
          skinId: skin.id,
          skinName: skin.name,
          hasPermission: skin.hasPermission,
          latestVersion: skin.latestVersion,
          validation
        });
      }
      
      return {
        ...skin,
        _validation: validation
      };
    });
    
    return validatedSkins;
  } catch (error) {
    console.error('스킨 검색 오류:', error);
    throw error;
  }
};

// 스킨 구매
export const purchaseSkin = async (skinId) => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    const url = `${apiBaseUrl}/api/skins/${skinId}/purchase`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      // 서버에서 받은 에러 메시지를 포함한 에러 객체 생성
      const error = new Error(data.message || `구매 요청 실패: ${response.status}`);
      error.response = { data };
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('스킨 구매 오류:', error);
    throw error;
  }
};

// 스킨 상세 정보 조회 (캐싱 적용)
export const fetchSkinDetail = async (skinId) => {
  const cacheKey = `detail_${skinId}`;
  const cached = apiCache.get(cacheKey);
  
  // 캐시가 유효하면 사용
  if (cached && (Date.now() - cached.timestamp < CACHE_TTL)) {
    return cached.data;
  }
  
  try {
    const apiBaseUrl = getApiBaseUrl();
    const url = `${apiBaseUrl}/api/skins/${skinId}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'max-age=300' // 서버에 5분 캐시 요청
      },
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`API 요청 실패: ${response.status}`);
    }
    
    const data = await response.json();
    
    // 성공 시 캐시 저장
    apiCache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
    
    return data;
  } catch (error) {
    console.error('스킨 상세 정보 조회 오류:', error);
    
    // 에러 시 오래된 캐시라도 사용
    if (cached) {
      console.warn(`[API] 에러 발생, 오래된 캐시 사용: ${cacheKey}`);
      return cached.data;
    }
    
    throw error;
  }
};

// 내 스킨 목록 조회
export const fetchMySkins = async (type = 'LAYOUT') => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    const url = `${apiBaseUrl}/api/skins/my?type=${type}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`API 요청 실패: ${response.status}`);
    }
    
    const data = await response.json();
    return data.skins || [];
  } catch (error) {
    console.error('내 스킨 목록 조회 오류:', error);
    throw error;
  }
};

// 특정 컴포넌트 타입의 보유한 스킨 조회
export const fetchOwnedComponentSkins = async (componentTypeName, category = null) => {
  try {
    // componentTypeName을 안전한 문자열로 변환
    const safeComponentTypeName = (() => {
      if (!componentTypeName) return 'COMPONENT';
      if (typeof componentTypeName === 'string') return componentTypeName;
      if (typeof componentTypeName === 'object') {
        if (typeof componentTypeName.id === 'string') return componentTypeName.id;
        if (typeof componentTypeName.name === 'string') return componentTypeName.name;
        if (typeof componentTypeName.displayName === 'string') return componentTypeName.displayName;
        return 'COMPONENT';
      }
      return 'COMPONENT';
    })();
    
    const apiBaseUrl = getApiBaseUrl();
    let url = `${apiBaseUrl}/api/skins/owned?type=COMPONENT&component_type_name=${safeComponentTypeName}`;
    
    if (category) {
      url += `&category=${category}`;
    }
    
    // 캐시 무효화를 위한 타임스탬프 추가
    url += `&_t=${Date.now()}`;
    

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`API 요청 실패: ${response.status}`);
    }
    
    const data = await response.json();

    const skins = data.skins || [];
    skins.forEach((skin, index) => {
    });
    
    return skins;
  } catch (error) {
    const errorComponentTypeName = componentTypeName || 'COMPONENT';
    console.error(`[fetchOwnedComponentSkins] ${errorComponentTypeName} 컴포넌트 보유 스킨 조회 오류:`, error);
    throw error;
  }
};

// 특정 컴포넌트 타입의 스킨 검색 (구매 가능한 모든 스킨)
export const searchComponentSkins = async (componentTypeName, query = '', category = null) => {
  try {
    // componentTypeName을 안전한 문자열로 변환
    const safeComponentTypeName = (() => {
      if (!componentTypeName) return 'COMPONENT';
      if (typeof componentTypeName === 'string') return componentTypeName;
      if (typeof componentTypeName === 'object') {
        if (typeof componentTypeName.id === 'string') return componentTypeName.id;
        if (typeof componentTypeName.name === 'string') return componentTypeName.name;
        if (typeof componentTypeName.displayName === 'string') return componentTypeName.displayName;
        return 'COMPONENT';
      }
      return 'COMPONENT';
    })();
    
    const apiBaseUrl = getApiBaseUrl();
    let url = `${apiBaseUrl}/api/skins/search?type=COMPONENT&component_type_name=${safeComponentTypeName}`;
    
    if (query) {
      url += `&q=${encodeURIComponent(query)}`;
    }
    
    if (category) {
      url += `&category=${category}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`API 요청 실패: ${response.status}`);
    }
    
    const data = await response.json();
    const skins = data.skins || [];
    

    const validatedSkins = skins.map(skin => {
      const validation = {
        id: !!skin.id,
        name: !!skin.name,
        hasPermission: skin.hasPermission !== undefined,
        latestVersion: !!skin.latestVersion,
        jsFileUrl: !!(skin.latestVersion && skin.latestVersion.jsFileUrl),
        cssFileUrl: !!(skin.latestVersion && skin.latestVersion.cssFileUrl),
        componentType: !!skin.componentType
      };
      
      if (!validation.jsFileUrl && skin.hasPermission) {
        console.warn(`⚠️ ${safeComponentTypeName} 컴포넌트 스킨 ${skin.name}(ID: ${skin.id})에 JS 파일 URL이 없습니다`, {
          skinId: skin.id,
          skinName: skin.name,
          componentType: skin.componentType,
          hasPermission: skin.hasPermission,
          latestVersion: skin.latestVersion,
          validation
        });
      }
      
      return {
        ...skin,
        _validation: validation
      };
    });
    
    return validatedSkins;
  } catch (error) {
    console.error(`${componentTypeName} 컴포넌트 스킨 검색 오류:`, error);
    throw error;
  }
};

// 내 컴포넌트 스킨 목록 조회
export const fetchMyComponentSkins = async (componentTypeName = null) => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    let url = `${apiBaseUrl}/api/skins/my?type=COMPONENT`;
    
    if (componentTypeName) {
      url += `&component_type_name=${componentTypeName}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`API 요청 실패: ${response.status}`);
    }
    
    const data = await response.json();
    return data.skins || [];
  } catch (error) {
    console.error('내 컴포넌트 스킨 목록 조회 오류:', error);
    throw error;
  }
};